import type { Animal, Feeding, Weight } from './types';

interface CacheData {
    animals: Animal[];
    feedings: Feeding[];
    weights: Weight[];
    lastSync: number;
}

interface PendingSync {
    id: string;
    type: 'animal' | 'feeding' | 'weight';
    operation: 'add' | 'update' | 'delete';
    data?: any;
    timestamp: number;
}

const CACHE_KEY = 'fosterpal-cache';
const PENDING_KEY = 'fosterpal-pending';
const CACHE_VERSION = 1;

class CacheManager {
    // Load cache from localStorage
    loadCache(): CacheData | null {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const parsed = JSON.parse(cached);

            // Validate cache version
            if (parsed.version !== CACHE_VERSION) {
                console.log('Cache version mismatch, clearing cache');
                this.clearCache();
                return null;
            }

            return parsed.data;
        } catch (error) {
            console.error('Error loading cache:', error);
            return null;
        }
    }

    // Save cache to localStorage
    saveCache(data: CacheData): void {
        try {
            const cacheObject = {
                version: CACHE_VERSION,
                data: {
                    ...data,
                    lastSync: Date.now(),
                },
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
        } catch (error) {
            console.error('Error saving cache:', error);
        }
    }

    // Clear cache
    clearCache(): void {
        localStorage.removeItem(CACHE_KEY);
    }

    // Get pending syncs
    getPendingSyncs(): PendingSync[] {
        try {
            const pending = localStorage.getItem(PENDING_KEY);
            return pending ? JSON.parse(pending) : [];
        } catch (error) {
            console.error('Error loading pending syncs:', error);
            return [];
        }
    }

    // Add pending sync
    addPendingSync(sync: Omit<PendingSync, 'timestamp'>): void {
        try {
            const pending = this.getPendingSyncs();
            pending.push({
                ...sync,
                timestamp: Date.now(),
            });
            localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        } catch (error) {
            console.error('Error adding pending sync:', error);
        }
    }

    // Remove pending sync
    removePendingSync(id: string): void {
        try {
            const pending = this.getPendingSyncs();
            const filtered = pending.filter(p => p.id !== id);
            localStorage.setItem(PENDING_KEY, JSON.stringify(filtered));
        } catch (error) {
            console.error('Error removing pending sync:', error);
        }
    }

    // Clear all pending syncs
    clearPendingSyncs(): void {
        localStorage.removeItem(PENDING_KEY);
    }

    // Update cache with new data (optimistic update)
    updateCacheOptimistically(
        type: 'animal' | 'feeding' | 'weight',
        operation: 'add' | 'update' | 'delete',
        data: any
    ): void {
        const cache = this.loadCache();
        if (!cache) return;

        switch (type) {
            case 'animal':
                if (operation === 'add') {
                    cache.animals.push(data);
                } else if (operation === 'update') {
                    const animalIndex = cache.animals.findIndex(a => a.id === data.id);
                    if (animalIndex !== -1) {
                        cache.animals[animalIndex] = data;
                    }
                } else if (operation === 'delete') {
                    cache.animals = cache.animals.filter(a => a.id !== data);
                }
                break;

            case 'feeding':
                if (operation === 'add') {
                    cache.feedings.unshift(data); // Add to beginning (most recent)
                    cache.feedings.sort((a, b) => b.timestamp - a.timestamp);
                }
                break;

            case 'weight':
                if (operation === 'add') {
                    cache.weights.unshift(data);
                    cache.weights.sort((a, b) => b.timestamp - a.timestamp);
                }
                break;
        }

        this.saveCache(cache);
    }

    // Check if cache is stale (older than 5 minutes)
    isCacheStale(): boolean {
        const cache = this.loadCache();
        if (!cache) return true;

        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() - cache.lastSync > fiveMinutes;
    }
}

export const cacheManager = new CacheManager();